import { useEffect, useRef, useState } from 'react';

import {
  deactivateUser as deactivateUserApi,
  getDeactivatePreflight,
  UserMgmtError,
  type DeactivatePreflight,
} from '../../../../api/users';
import type { UserRow } from '../userViewModel';

import DeactivateConfirmFooter from './DeactivateConfirmFooter';
import DeactivateErrorBanner from './DeactivateErrorBanner';
import DeactivateHeader from './DeactivateHeader';
import DeactivateLoadingBanner from './DeactivateLoadingBanner';
import DeactivateReviewBody from './DeactivateReviewBody';
import DeactivateReviewFooter from './DeactivateReviewFooter';
import ModalScrim from './ModalScrim';

interface DeactivateUserModalProps {
  user: UserRow;
  onClose: () => void;
  /** Called after the PATCH succeeds. Page refetches the staff list. */
  onDeactivated: () => void;
}

/**
 * Map a backend error code to operator-friendly modal text.
 */
const ERROR_TEXT: Record<string, string> = {
  LAST_ADMIN_GUARD:
    "Can't deactivate the last active Admin. Promote another user to Admin first, then retry.",
  SELF_TARGET: "You can't deactivate your own account. Ask another Admin to make this change.",
  TARGET_INACTIVE: 'This user is already deactivated.',
  TARGET_NOT_FOUND: 'User not found. They may have been removed in another session.',
  RD_TARGET_OUT_OF_SCOPE: 'This user has regions outside your scope. Ask an Admin to deactivate.',
  TARGET_OUTRANKS_CALLER: 'Only an Admin can change this user.',
};

function explainError(err: unknown): string {
  if (!(err instanceof UserMgmtError)) {
    return err instanceof Error ? err.message : 'Deactivate failed. Please retry.';
  }
  return (err.code && ERROR_TEXT[err.code]) || err.message;
}

/**
 * Two-step deactivate modal (BON-55). Step 1 fetches a fresh
 * `GET /:id/deactivate-preflight` and shows the owned-records count;
 * step 2 is a final confirm before firing `PATCH /:id/deactivate`.
 *
 * Backend error codes (LAST_ADMIN_GUARD, SELF_TARGET, etc.) surface as
 * friendly banners via the central `ERROR_TEXT` lookup.
 *
 * Complexity comes from the matrix of (loadError, preflight, step,
 * saveError) states the render branches on; extracting helpers would
 * obscure the state machine. Disabled here with a precise per-function
 * directive (mirrors BON-54's editUserRoles pattern).
 */
// eslint-disable-next-line complexity, max-lines-per-function -- see comment above
export default function DeactivateUserModal({
  user,
  onClose,
  onDeactivated,
}: DeactivateUserModalProps) {
  const [step, setStep] = useState<'review' | 'confirm'>('review');
  const [preflight, setPreflight] = useState<DeactivatePreflight | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  /** Tracks whether the modal is still mounted so async setters in
   *  handleSubmit don't fire on an unmounted component (BON-55 review
   *  I5). The preflight useEffect has its own cleanup; this ref covers
   *  the submit path. */
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    getDeactivatePreflight(user.id)
      .then((p) => {
        if (cancelled) return;
        setPreflight(p);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(explainError(err));
      });
    return () => {
      cancelled = true;
    };
  }, [user.id]);

  async function handleSubmit(): Promise<void> {
    setSubmitting(true);
    setSaveError(null);
    try {
      await deactivateUserApi(user.id);
      // onDeactivated triggers the page-level refetch + dialog close;
      // it's safe to call even after unmount (parent owns the dialog
      // state). Don't gate it on mountedRef.
      onDeactivated();
    } catch (err) {
      if (!mountedRef.current) return;
      setSaveError(explainError(err));
      setSubmitting(false);
    }
  }

  return (
    <ModalScrim width={560} onClose={onClose} labelledBy="deactivate-title">
      <DeactivateHeader user={user} step={step} />
      {loadError ? (
        <DeactivateErrorBanner message={loadError} kind="load" />
      ) : !preflight ? (
        <DeactivateLoadingBanner />
      ) : (
        <DeactivateReviewBody preflight={preflight} onNavigateAway={onClose} />
      )}
      {saveError && <DeactivateErrorBanner message={saveError} kind="save" />}
      {preflight && !loadError && step === 'review' && (
        <DeactivateReviewFooter onCancel={onClose} onContinue={() => setStep('confirm')} />
      )}
      {preflight && !loadError && step === 'confirm' && (
        <DeactivateConfirmFooter
          submitting={submitting}
          onBack={() => {
            setSaveError(null);
            setStep('review');
          }}
          onSubmit={handleSubmit}
        />
      )}
      {(loadError || (!preflight && !loadError)) && (
        <div className="px-[22px] py-3.5 border-t border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-[#1e2520] flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="h-8 px-3.5 rounded-lg text-[13px] font-semibold text-stone-600 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-[#242d28]"
          >
            Close
          </button>
        </div>
      )}
    </ModalScrim>
  );
}
